import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import Axios from "axios";
import Constant from "../../Constant";
import { getAccessToken } from "../../../hooks/AccessToken";
import "../../../css/style.css";

const initialState = {
  name: "",
  description: "",
  image: null,
  videoLink: "",
  startDate: "",
  endDate: "",
  status: "1",
};

const AddUpdateSubEvent = () => {
  const { id, subEventId } = useParams();
  const eventId = id;
  const isEdit = Boolean(subEventId);
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [changedFields, setChangedFields] = useState(new Set());
  const fileInputRef = useRef();
  const history = useHistory();

  // Helper to convert UTC ISO string to local IST datetime-local string
  const utcToISTLocal = (utcString) => {
    if (!utcString) return "";
    const date = new Date(utcString);
    const istOffset = 5.5 * 60;
    const istDate = new Date(date.getTime() + istOffset * 60000);
    return istDate.toISOString().slice(0, 16);
  };

  // Helper to convert local IST datetime-local string to UTC ISO string
  const istLocalToUTCISO = (localString) => {
    if (!localString) return "";
    const [datePart, timePart] = localString.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);
    const istDate = new Date(
      Date.UTC(year, month - 1, day, hour - 5, minute - 30)
    );
    return new Date(istDate.getTime() + 5.5 * 60 * 60000).toISOString();
  };

  useEffect(() => {
    if (isEdit) {
      fetchSubEvent();
    }
  }, [subEventId]);

  const fetchSubEvent = async () => {
    setLoading(true);
    setError("");
    try {
      const token = getAccessToken();
      const response = await Axios.get(
        Constant.apiBasePath + `simple-sub-events/admin/get/${subEventId}`,
        { headers: { token } }
      );
      if (response.data.status === Constant.statusSuccess) {
        const data = response.data.data;
        setForm({
          name: data.name || "",
          description: data.description || "",
          image: null,
          videoLink: data.videoLink || "",
          startDate: utcToISTLocal(data.startDate),
          endDate: utcToISTLocal(data.endDate),
          status: data.status !== undefined ? String(data.status) : "1",
        });
        setImagePreview(null);
        setChangedFields(new Set());
      } else {
        setError(response.data.message || "Failed to fetch sub event");
      }
    } catch (err) {
      setError("Error fetching sub event");
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
    setChangedFields((prev) => {
      const updated = new Set(prev);
      updated.add(name);
      return updated;
    });
    if (type === "file" && files[0]) {
      setImagePreview(URL.createObjectURL(files[0]));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    const token = getAccessToken();
    const formData = new FormData();
    const formToSend = {
      ...form,
      startDate: form.startDate ? istLocalToUTCISO(form.startDate) : "",
      endDate: form.endDate ? istLocalToUTCISO(form.endDate) : "",
      eventId: eventId,
    };
    if (isEdit) {
      changedFields.forEach((field) => {
        if (field === "image" && form.image) {
          formData.append("image", form.image);
        } else if (field === "startDate" || field === "endDate") {
          formData.append(field, formToSend[field]);
        } else if (field !== "image") {
          formData.append(field, form[field]);
        }
      });
    } else {
      Object.entries(formToSend).forEach(([key, value]) => {
        if (key === "image" && value) {
          formData.append("image", value);
        } else if (key !== "image") {
          formData.append(key, value);
        }
      });
    }
    try {
      let response;
      if (isEdit) {
        response = await Axios.put(
          Constant.apiBasePath + `simple-sub-events/admin/update/${subEventId}`,
          formData,
          { headers: { token } }
        );
      } else {
        response = await Axios.post(
          Constant.apiBasePath + "simple-sub-events/create",
          formData,
          { headers: { token } }
        );
      }
      if (response.data.status === Constant.statusSuccess) {
        setSuccess(
          isEdit
            ? "Sub Event updated successfully"
            : "Sub Event created successfully"
        );
        setTimeout(
          () => history.push(`/simple-events/${eventId}/sub-events`),
          1200
        );
      } else {
        setError(response.data.message || "Operation failed");
      }
    } catch (err) {
      setError("Error submitting form");
    }
    setLoading(false);
  };

  const heading = isEdit ? "Edit Sub Event" : "Add Sub Event";

  return (
    <div className="content-page contest-add-update-page simple-events-consistent-page">
      <div className="content page-content-spaced">
        <div className="event-management-header">
          <div className="header-content">
            <div className="header-title">
              <h4 className="page-title-heading">{heading}</h4>
              <p className="header-subtitle">
                Configure sub event details, dates, media, and status.
              </p>
            </div>
            <div className="header-actions">
              <Link to={`/simple-events/${eventId}/sub-events`} className="btn openmic-link-btn">
                Back to Sub Events
              </Link>
            </div>
          </div>
        </div>

        <div className="event-management-body">
          <div className="row">
            <div className="col-12 contest-form-column">
              <div className="card contest-form-card">
                <div className="card-body contest-form-card-body">
                {loading && <div>Loading...</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                {success && (
                  <div className="alert alert-success">{success}</div>
                )}
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  <div className="form-group mb-3">
                    <label>Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label>Image</label>
                    <input
                      type="file"
                      className="form-control-file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      ref={fileInputRef}
                    />
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ maxWidth: 200, marginTop: 10 }}
                      />
                    )}
                  </div>
                  <div className="form-group mb-3">
                    <label>Description *</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label>Video Link</label>
                    <input
                      type="text"
                      className="form-control"
                      name="videoLink"
                      value={form.videoLink}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label>Start Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="startDate"
                      value={form.startDate}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label>End Date *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      name="endDate"
                      value={form.endDate}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label>Status *</label>
                    <select
                      className="form-control"
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      required
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-add-event contest-add-match"
                    disabled={loading}
                  >
                    {isEdit ? "Update" : "Create"}
                  </button>
                  <Link
                    to={`/simple-events/${eventId}/sub-events`}
                    className="btn openmic-link-btn ml-2"
                  >
                    Cancel
                  </Link>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AddUpdateSubEvent;
